import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import jsPDF from 'jspdf';
import { toast } from 'react-hot-toast';
import InvoiceSummaryReport from '../components/reports/InvoiceSummaryReport';
import ReportTable from '../components/reports/ReportTable';
import ReportSummary from '../components/reports/ReportSummary';
import InvoiceViewModalNew from '../components/InvoiceViewModalNew';
import {
  DocumentTextIcon,
  CurrencyDollarIcon,
  UsersIcon,
  ShoppingBagIcon,
  ServerIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  ChartPieIcon
} from '@heroicons/react/24/outline';

const Reports = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('invoice-summary');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: '',
    province: '',
    limit: 20,
    folderId: ''
  });

  // Load folders for the folder filter
  useEffect(() => {
    const fetchFolders = async () => {
      try {
        const res = await api.folders.getAll();
        setFolders(res.data?.folders || []);
      } catch (e) {
        console.error('❌ Failed to load folders:', e);
      }
    };
    fetchFolders();
  }, []);

  // Report categories and their reports
  const reportCategories = [
    {
      id: 'invoice',
      name: 'Invoice Reports',
      icon: DocumentTextIcon,
      color: 'blue',
      reports: [
        { id: 'invoice-summary', name: 'Invoice Summary', description: 'Overview of all invoices with key metrics' },
        { id: 'invoice-details', name: 'Invoice Details', description: 'Detailed list of all invoices' },
        { id: 'sales-by-province', name: 'Sales by Province', description: 'Sales breakdown by destination province' },
        { id: 'monthly-trends', name: 'Monthly Trends', description: 'Monthly sales and invoice trends' }
      ]
    },
    {
      id: 'customer',
      name: 'Customer Reports',
      icon: UsersIcon,
      color: 'green',
      reports: [
        { id: 'top-customers', name: 'Top Customers', description: 'Highest value customers by sales volume' }
      ]
    },
    {
      id: 'product',
      name: 'Product Reports',
      icon: ShoppingBagIcon,
      color: 'purple',
      reports: [
        { id: 'top-products', name: 'Top Products', description: 'Best selling products by revenue' },
        { id: 'hs-code-analysis', name: 'HS Code Analysis', description: 'Analysis by HS code categories' }
      ]
    },
    {
      id: 'tax',
      name: 'Tax Reports',
      icon: CurrencyDollarIcon,
      color: 'yellow',
      reports: [
        { id: 'tax-summary', name: 'Tax Summary', description: 'Tax collection and withholding summary' }
      ]
    }
  ];

  // Add admin reports if user is admin
  if (user?.role === 'admin') {
    reportCategories.push({
      id: 'system',
      name: 'System Reports',
      icon: ServerIcon,
      color: 'red',
      reports: [
        { id: 'api-usage', name: 'API Usage', description: 'API endpoint usage statistics' },
        { id: 'user-activity', name: 'User Activity', description: 'User activity and engagement metrics' }
      ]
    });
  }

  const loadReport = useCallback(async (reportId) => {
    try {
      setLoading(true);
      setReportData(null);

      console.log(`🔄 Loading report: ${reportId} with filters:`, filters);

      // Build query parameters, only include non-empty values
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== '') {
          params[key] = value;
        }
      });

      console.log(`📊 API call params:`, params);

      // Use the specific API method based on report type
      let response;
      switch (reportId) {
        case 'invoice-summary':
          response = await api.reports.getInvoiceSummary(params);
          break;
        case 'invoice-details':
          response = await api.reports.getInvoiceDetails(params);
          break;
        case 'sales-by-province':
          response = await api.reports.getSalesByProvince(params);
          break;
        case 'monthly-trends':
          response = await api.reports.getMonthlyTrends(params);
          break;
        case 'top-customers':
          response = await api.reports.getTopCustomers(params);
          break;
        case 'top-products':
          response = await api.reports.getTopProducts(params);
          break;
        case 'hs-code-analysis':
          response = await api.reports.getHsCodeAnalysis(params);
          break;
        case 'tax-summary':
          response = await api.reports.getTaxSummary(params);
          break;
        case 'api-usage':
          response = await api.reports.getApiUsage(params);
          break;
        case 'user-activity':
          response = await api.reports.getUserActivity(params);
          break;
        default:
          throw new Error(`Unknown report type: ${reportId}`);
      }

      console.log(`✅ Report data loaded:`, response.data);
      setReportData(response.data);
    } catch (error) {
      console.error('❌ Failed to load report:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load report data';
      setReportData({
        error: errorMessage,
        details: error.response?.data?.details || 'Please check your connection and try again.'
      });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (activeTab) {
      loadReport(activeTab);
    }
  }, [activeTab, loadReport]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleInvoiceRowClick = async (row) => {
    if (!row?.id) return;

    try {
      setSelectedInvoice(null);
      setShowInvoiceModal(false);
      const response = await api.invoices.getById(row.id);
      setSelectedInvoice(response.data.invoice);
      setShowInvoiceModal(true);
    } catch (error) {
      console.error('Failed to load invoice details:', error);
      toast.error('Failed to load invoice details');
    }
  };

  const buildFilterParams = (extra = {}) => {
    const params = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '') {
        params[key] = value;
      }
    });
    return { ...params, ...extra };
  };

  const getReportColumns = (reportType) => {
    if (reportType === 'invoice-details') {
      return [
        { key: 'invoice_date', label: 'Date', type: 'date' },
        { key: 'invoice_number', label: 'Invoice No' },
        { key: 'buyer_business_name', label: 'Customer' },
        { key: 'buyer_ntn_cnic', label: 'NTN/CNIC' },
        { key: 'hs_codes', label: 'HS Code' },
        { key: 'destination_of_supply', label: 'Province' },
        { key: 'total_sale_value', label: 'Sales Value', type: 'currency' },
        { key: 'total_tax_charged', label: 'Tax', type: 'currency' },
        { key: 'status', label: 'Status' },
        { key: 'item_count', label: 'Items', type: 'number' }
      ];
    }

    if (Array.isArray(reportData?.data) && reportData.data[0]) {
      return Object.keys(reportData.data[0]).map(key => ({
        key,
        label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      }));
    }

    return [];
  };

  const loadExportData = async () => {
    if (activeTab === 'invoice-details') {
      const total = reportData?.pagination?.total || filters.limit || 1000;
      const response = await api.reports.getInvoiceDetails(buildFilterParams({
        page: 1,
        limit: Math.max(total, 1)
      }));
      return response.data?.data || [];
    }

    return reportData?.data;
  };

  const formatExportValue = (value, column = {}) => {
    if (value == null) return '';
    if (column.type === 'date') return new Date(value).toLocaleDateString();
    if (column.type === 'currency') return Number(value || 0).toFixed(2);
    return String(value);
  };

  const exportReportPDF = (data, reportType) => {
    if (!Array.isArray(data) || data.length === 0) {
      alert('No rows available for PDF export.');
      return;
    }

    const columns = getReportColumns(reportType);
    const pdf = new jsPDF('l', 'pt', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 28;
    const rowHeight = 18;
    const usableWidth = pageWidth - margin * 2;
    const invoiceDetailWidths = {
      invoice_date: 58,
      invoice_number: 145,
      buyer_business_name: 100,
      buyer_ntn_cnic: 70,
      hs_codes: 78,
      destination_of_supply: 72,
      total_sale_value: 72,
      total_tax_charged: 62,
      status: 60,
      item_count: 35
    };
    const fixedWidths = reportType === 'invoice-details'
      ? columns.map(column => invoiceDetailWidths[column.key] || 70)
      : null;
    const totalFixedWidth = fixedWidths?.reduce((sum, width) => sum + width, 0) || 0;
    const scale = fixedWidths && totalFixedWidth > usableWidth ? usableWidth / totalFixedWidth : 1;
    const columnWidths = fixedWidths
      ? fixedWidths.map(width => width * scale)
      : columns.map(() => usableWidth / columns.length);
    const columnXPositions = columnWidths.reduce((positions, width, index) => {
      positions.push(index === 0 ? margin : positions[index - 1] + columnWidths[index - 1]);
      return positions;
    }, []);
    let y = 52;

    const fitText = (value, maxWidth) => {
      const text = String(value ?? '');
      if (!text) return '';
      const availableWidth = Math.max(maxWidth - 6, 10);
      if (pdf.getTextWidth(text) <= availableWidth) return text;

      let clipped = text;
      while (clipped.length > 1 && pdf.getTextWidth(`${clipped}...`) > availableWidth) {
        clipped = clipped.slice(0, -1);
      }
      return `${clipped}...`;
    };

    const drawHeader = () => {
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.text(`${currentReport?.name || reportType} Report`, margin, 28);
      pdf.setFontSize(8);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Generated: ${new Date().toLocaleString()} | Rows: ${data.length}`, margin, 42);
      pdf.setFillColor(239, 246, 255);
      pdf.rect(margin, y - 12, pageWidth - margin * 2, rowHeight, 'F');
      pdf.setFont(undefined, 'bold');
      columns.forEach((column, index) => {
        pdf.text(fitText(column.label, columnWidths[index]), columnXPositions[index] + 3, y);
      });
      pdf.setFont(undefined, 'normal');
      y += rowHeight;
    };

    drawHeader();
    pdf.setFontSize(reportType === 'invoice-details' ? 7 : 8);

    data.forEach(row => {
      if (y > pageHeight - margin) {
        pdf.addPage();
        y = 52;
        drawHeader();
        pdf.setFontSize(reportType === 'invoice-details' ? 7 : 8);
      }

      columns.forEach((column, index) => {
        const value = formatExportValue(row[column.key], column);
        pdf.text(fitText(value, columnWidths[index]), columnXPositions[index] + 3, y);
      });
      y += rowHeight;
    });

    const timestamp = new Date().toISOString().split('T')[0];
    pdf.save(`${reportType}-report-${timestamp}.pdf`);
  };

  const exportReport = async (format = 'csv') => {
    try {
      if (!reportData || !reportData.data) {
        alert('No data available to export. Please load a report first.');
        return;
      }

      console.log(`📥 Exporting ${activeTab} report as ${format}`);

      const exportData = await loadExportData();
      if (format === 'pdf') {
        exportReportPDF(exportData, activeTab);
        return;
      }

      const csvData = convertToCSV(exportData, activeTab, getReportColumns(activeTab));

      // Create and download the file
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${activeTab}-report-${timestamp}.${format}`;
      link.setAttribute('download', filename);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      console.log(`✅ Report exported as: ${filename}`);
    } catch (error) {
      console.error('❌ Failed to export report:', error);
      alert('Failed to export report. Please try again.');
    }
  };

  const convertToCSV = (data, reportType, selectedColumns = null) => {
    if (!Array.isArray(data)) {
      // Handle summary reports (single object)
      if (reportType === 'invoice-summary') {
        const headers = ['Metric', 'Value'];
        const rows = [
          ['Total Invoices', data.total_invoices || 0],
          ['Total Sales Value', `PKR ${(data.total_sales_value || 0).toLocaleString()}`],
          ['Total Tax Charged', `PKR ${(data.total_tax_charged || 0).toLocaleString()}`],
          ['Average Invoice Value', `PKR ${(data.avg_invoice_value || 0).toLocaleString()}`],
          ['Submitted Count', data.submitted_count || 0],
          ['Validated Count', data.validated_count || 0],
          ['Pending Count', data.pending_count || 0],
          ['Error Count', data.error_count || 0],
          ['Success Rate', `${Math.round(((data.submitted_count || 0) / (data.total_invoices || 1)) * 100)}%`]
        ];

        return [headers, ...rows].map(row =>
          row.map(cell => `"${cell}"`).join(',')
        ).join('\n');
      }
      return 'No data available';
    }

    if (data.length === 0) {
      return 'No data available';
    }

    const columns = selectedColumns?.length
      ? selectedColumns
      : Object.keys(data[0]).map(key => ({ key, label: key }));
    const headers = columns.map(column => column.label);

    // Convert data to CSV rows
    const csvRows = [
      headers.join(','), // Header row
      ...data.map(row =>
        columns.map(column => {
          const value = formatExportValue(row[column.key], column);
          // Handle null/undefined values and escape quotes
          const cellValue = value == null ? '' : String(value).replace(/"/g, '""');
          return `"${cellValue}"`;
        }).join(',')
      )
    ];

    return csvRows.join('\n');
  };

  const getCurrentReport = () => {
    for (const category of reportCategories) {
      const report = category.reports.find(r => r.id === activeTab);
      if (report) return { ...report, category };
    }
    return null;
  };

  const renderReportContent = () => {
    if (!reportData) return null;

    switch (activeTab) {
      case 'invoice-summary':
        return <InvoiceSummaryReport data={reportData} />;

      case 'invoice-details':
        const invoiceColumns = getReportColumns('invoice-details').map(column =>
          column.key === 'status' ? { ...column, type: 'status' } : column
        );
        return (
          <ReportTable
            data={reportData.data}
            columns={invoiceColumns}
            title="Invoice Details"
            pagination={reportData.pagination}
            onRowClick={handleInvoiceRowClick}
          />
        );

      case 'sales-by-province':
        const provinceColumns = [
          { key: 'province', label: 'Province' },
          { key: 'invoice_count', label: 'Invoices', type: 'number' },
          { key: 'total_sales', label: 'Total Sales', type: 'currency' },
          { key: 'total_tax', label: 'Total Tax', type: 'currency' },
          { key: 'avg_invoice_value', label: 'Avg Invoice', type: 'currency' },
          { key: 'submitted_count', label: 'Submitted', type: 'number' },
          { key: 'error_count', label: 'Errors', type: 'number' }
        ];
        return (
          <div>
            {reportData.totals && (
              <ReportSummary
                totals={reportData.totals}
                title="Sales by Province Summary"
              />
            )}
            <ReportTable
              data={reportData.data}
              columns={provinceColumns}
              title="Sales by Province"
            />
          </div>
        );

      case 'monthly-trends':
        const monthlyColumns = [
          { key: 'month', label: 'Month' },
          { key: 'invoice_count', label: 'Invoices', type: 'number' },
          { key: 'total_sales', label: 'Sales', type: 'currency' },
          { key: 'total_tax', label: 'Tax', type: 'currency' },
          { key: 'avg_invoice_value', label: 'Avg Value', type: 'currency' },
          { key: 'submitted_count', label: 'Submitted', type: 'number' }
        ];
        return (
          <div>
            {reportData.totals && (
              <ReportSummary
                totals={reportData.totals}
                title="Monthly Trends Summary"
              />
            )}
            <ReportTable
              data={reportData.data}
              columns={monthlyColumns}
              title="Monthly Trends"
            />
          </div>
        );

      case 'top-customers':
        const customerColumns = [
          { key: 'customer_name', label: 'Customer Name' },
          { key: 'buyer_province', label: 'Province' },
          { key: 'invoice_count', label: 'Invoices', type: 'number' },
          { key: 'total_purchases', label: 'Total Purchases', type: 'currency' },
          { key: 'avg_invoice_value', label: 'Avg Invoice', type: 'currency' },
          { key: 'last_purchase_date', label: 'Last Purchase', type: 'date' }
        ];
        return (
          <div>
            {reportData.totals && (
              <ReportSummary
                totals={reportData.totals}
                title="Top Customers Summary"
              />
            )}
            <ReportTable
              data={reportData.data}
              columns={customerColumns}
              title="Top Customers"
            />
          </div>
        );

      case 'top-products':
        const productColumns = [
          { key: 'hs_code', label: 'HS Code' },
          { key: 'product_description', label: 'Product Description' },
          { key: 'uom', label: 'UoM' },
          { key: 'times_sold', label: 'Times Sold', type: 'number' },
          { key: 'total_quantity', label: 'Total Qty', type: 'number' },
          { key: 'total_revenue', label: 'Total Revenue', type: 'currency' },
          { key: 'total_tax', label: 'Total Tax', type: 'currency' },
          { key: 'avg_rate', label: 'Avg Rate', type: 'currency' }
        ];
        return (
          <div>
            {reportData.data && reportData.data.length > 0 ? (
              <ReportTable
                data={reportData.data}
                columns={productColumns}
                title="Top Products by Revenue"
              />
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No product data found. This could be because:</p>
                <ul className="text-sm text-gray-400 mt-2">
                  <li>• No invoices have items with valid HS codes</li>
                  <li>• Items don't have revenue data (rate × quantity)</li>
                  <li>• Try adjusting your date filters</li>
                </ul>
              </div>
            )}
          </div>
        );

      case 'hs-code-analysis':
        const hsCodeColumns = [
          { key: 'hs_chapter', label: 'HS Chapter' },
          { key: 'unique_codes_used', label: 'Unique Codes', type: 'number' },
          { key: 'total_transactions', label: 'Transactions', type: 'number' },
          { key: 'total_revenue', label: 'Total Revenue', type: 'currency' },
          { key: 'total_tax', label: 'Total Tax', type: 'currency' },
          { key: 'total_quantity', label: 'Total Quantity', type: 'number' },
          { key: 'avg_rate', label: 'Avg Rate', type: 'currency' }
        ];
        return (
          <div>
            {reportData.data && reportData.data.length > 0 ? (
              <ReportTable
                data={reportData.data}
                columns={hsCodeColumns}
                title="HS Code Analysis by Chapter"
              />
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No HS Code data found. This could be because:</p>
                <ul className="text-sm text-gray-400 mt-2">
                  <li>• No invoices have items with valid HS codes (minimum 4 digits)</li>
                  <li>• Items don't have revenue data</li>
                  <li>• Try adjusting your date filters</li>
                </ul>
              </div>
            )}
          </div>
        );

      case 'tax-summary':
        const taxColumns = [
          { key: 'period', label: 'Period' },
          { key: 'invoice_count', label: 'Invoices', type: 'number' },
          { key: 'total_sales_value', label: 'Sales Value', type: 'currency' },
          { key: 'total_tax_charged', label: 'Tax Charged', type: 'currency' },
          { key: 'total_amount_withheld', label: 'Withheld', type: 'currency' },
          { key: 'avg_tax_rate_percent', label: 'Avg Tax Rate', type: 'percentage' }
        ];
        return (
          <ReportTable
            data={reportData.data}
            columns={taxColumns}
            title="Tax Summary"
          />
        );

      case 'api-usage':
        const apiColumns = [
          { key: 'endpoint', label: 'Endpoint' },
          { key: 'method', label: 'Method' },
          { key: 'request_count', label: 'Requests', type: 'number' },
          { key: 'avg_response_time', label: 'Avg Response (ms)', type: 'number' },
          { key: 'success_count', label: 'Success', type: 'number' },
          { key: 'error_count', label: 'Errors', type: 'number' }
        ];
        return (
          <ReportTable
            data={reportData.data}
            columns={apiColumns}
            title="API Usage Statistics"
          />
        );

      case 'user-activity':
        const userColumns = [
          { key: 'business_name', label: 'Business Name' },
          { key: 'email', label: 'Email' },
          { key: 'license_type', label: 'License' },
          { key: 'total_invoices', label: 'Total Invoices', type: 'number' },
          { key: 'total_sales_value', label: 'Sales Value', type: 'currency' },
          { key: 'submitted_invoices', label: 'Submitted', type: 'number' },
          { key: 'last_invoice_date', label: 'Last Activity', type: 'datetime' }
        ];
        return (
          <ReportTable
            data={reportData.data}
            columns={userColumns}
            title="User Activity"
          />
        );

      default:
        return (
          <pre className="bg-gray-50 p-4 rounded-md overflow-auto text-sm">
            {JSON.stringify(reportData, null, 2)}
          </pre>
        );
    }
  };

  const currentReport = getCurrentReport();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
            <p className="mt-1 text-sm text-gray-600">
              Comprehensive reporting and analytics for your invoice data
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => loadReport(activeTab)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Refresh
            </button>
            {reportData && !reportData.error && (
              <>
                <button
                  onClick={() => exportReport('csv')}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Export CSV
                </button>
                <button
                  onClick={() => exportReport('pdf')}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-800 hover:bg-gray-900"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Export PDF
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Report Categories Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Report Categories</h3>
            <nav className="space-y-2">
              {reportCategories.map((category) => {
                const Icon = category.icon;
                const isActive = category.reports.some(r => r.id === activeTab);
                
                return (
                  <div key={category.id}>
                    <div className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                      isActive 
                        ? `bg-${category.color}-100 text-${category.color}-700` 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}>
                      <Icon className="h-5 w-5 mr-3" />
                      {category.name}
                    </div>
                    <div className="ml-8 mt-1 space-y-1">
                      {category.reports.map((report) => (
                        <button
                          key={report.id}
                          onClick={() => setActiveTab(report.id)}
                          className={`block w-full text-left px-3 py-2 text-sm rounded-md ${
                            activeTab === report.id
                              ? `bg-${category.color}-50 text-${category.color}-700 border-l-2 border-${category.color}-500`
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {report.name}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Report Area */}
        <div className="lg:col-span-3">
          {/* Report Header */}
          {currentReport && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{currentReport.name}</h2>
                  <p className="mt-1 text-sm text-gray-600">{currentReport.description}</p>
                  {reportData && !reportData.error && (
                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                      {Array.isArray(reportData.data) ? (
                        <span>📊 {reportData.data.length} records found</span>
                      ) : reportData.data ? (
                        <span>📊 Summary data loaded</span>
                      ) : null}
                      {reportData.pagination && (
                        <span>📄 Page {reportData.pagination.page} of {reportData.pagination.totalPages}</span>
                      )}
                      <span>🕒 Last updated: {new Date().toLocaleTimeString()}</span>
                    </div>
                  )}
                </div>
                <div className={`p-3 rounded-full bg-${currentReport.category.color}-100`}>
                  <currentReport.category.icon className={`h-6 w-6 text-${currentReport.category.color}-600`} />
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <div className="flex items-center mb-4">
              <FunnelIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Filters</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">All Statuses</option>
                  <option value="Pending">Pending</option>
                  <option value="Validated">Validated</option>
                  <option value="Submitted">Submitted</option>
                  <option value="Error">Error</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Limit</label>
                <select
                  value={filters.limit}
                  onChange={(e) => handleFilterChange('limit', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Folder</label>
                <select
                  value={filters.folderId}
                  onChange={(e) => handleFilterChange('folderId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">All Folders</option>
                  {folders.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Report Content */}
          <div className="bg-white shadow rounded-lg">
            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-flex items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                  <span className="text-lg text-gray-600">Loading report...</span>
                </div>
              </div>
            ) : reportData?.error ? (
              <div className="p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="text-red-600 text-lg font-medium mb-2">
                    {reportData.error}
                  </div>
                  {reportData.details && (
                    <div className="text-gray-600 text-sm mb-4">
                      {reportData.details}
                    </div>
                  )}
                  <button
                    onClick={() => loadReport(activeTab)}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Retry
                  </button>
                </div>
              </div>
            ) : reportData ? (
              <div className="p-6">
                {renderReportContent()}
              </div>
            ) : (
              <div className="p-12 text-center">
                <ChartPieIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No Data</h3>
                <p className="mt-1 text-sm text-gray-500">Select a report to view data.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <InvoiceViewModalNew
        key={selectedInvoice?.id || 'report-invoice'}
        invoice={selectedInvoice}
        isOpen={showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false);
          setSelectedInvoice(null);
        }}
      />
    </div>
  );
};

export default Reports;
